/* eslint-disable no-underscore-dangle */
import { setUser } from '@sentry/react';
import { getConfig } from '@utils/config';
import { JwtExpiredError } from '@utils/errors';
import { MockLocalStorage } from '@utils/mockLocalStorage';
import { type Booleanish, IS_NODE } from '@utils/types';
import jwtDecode, { InvalidTokenError } from 'jwt-decode';
import { action, computed, makeObservable, observable } from 'mobx';
import { ZodError, z } from 'zod';

const AUTH_TYPE = 'd2d-client-auth-type';
const USER_EMAIL_KEY = 'd2d-client-user-email-key';
const KEY = 'd2d-client-key';
const REFRESH = 'd2d-client-refresh';
const COOKIE_ACCEPTED = 'd2d-client-cookie-accepted';
const IMPERSONATE_USER_ID = 'Impersonate-User-Id';
const IMPERSONATE_ORIGINAL_USER_ID = 'Impersonate-Original-User-Id';

export const AuthManagerUser = z.object({
  auth_type: z.string().nullable(),
  email: z.string(),
  key: z.string(),
  refresh: z.string().nullable(),
  impersonatedUser: z.coerce.number().nullable(),
  originalUser: z.coerce.number().nullable(),
});

export type AuthManagerUser = z.infer<typeof AuthManagerUser>;

const JWTDecoded = z.object({
  exp: z.number(),
});

export type JWTDecoded = z.infer<typeof JWTDecoded>;

export class AuthManager {
  static instance: AuthManager | undefined;

  @observable
  user: AuthManagerUser | undefined;

  @observable
  cookieAccepted: Booleanish = 'false';

  @observable
  cookieDismissed: boolean = false;

  isLoggingOut: boolean = false;

  storage = globalThis.localStorage ?? new MockLocalStorage();

  tokenPromise: Promise<string> | null = null;

  static get shared() {
    if (AuthManager.instance === undefined) {
      AuthManager.instance = new AuthManager();
    }

    return AuthManager.instance;
  }

  constructor() {
    const cookieAccepted = this.storage.getItem(COOKIE_ACCEPTED);
    if (typeof cookieAccepted === 'string') {
      this.cookieAccepted = cookieAccepted as Booleanish;
      this.cookieDismissed = true;
    }

    const userResult = AuthManagerUser.safeParse({
      auth_type: this.storage.getItem(AUTH_TYPE),
      email: this.storage.getItem(USER_EMAIL_KEY),
      key: this.storage.getItem(KEY),
      refresh: this.storage.getItem(REFRESH),
      impersonatedUser: this.storage.getItem(IMPERSONATE_USER_ID),
      originalUser: this.storage.getItem(IMPERSONATE_ORIGINAL_USER_ID),
    });

    if (userResult.success) {
      this.setUser(userResult.data);
    }

    if (import.meta.env.VITE_APP_STAGE !== 'e2e') {
      this.clearCookie();
    }

    makeObservable(this);
  }

  @action
  setLocalStorage(key: string, value: string) {
    if (!IS_NODE && key !== COOKIE_ACCEPTED && this.cookieAccepted !== 'true') {
      throw new Error('Failed to set cookie because user does not agree. ');
    }
    this.storage.setItem(key, value);
  }

  @action
  setUser(user: AuthManagerUser) {
    this.user = user;
    if (user.auth_type) {
      this.setLocalStorage(AUTH_TYPE, user.auth_type);
    } else {
      this.setLocalStorage(AUTH_TYPE, 'Token');
    }
    this.setLocalStorage(USER_EMAIL_KEY, user.email);
    this.setLocalStorage(KEY, user.key);
    if (user.refresh) {
      this.setLocalStorage(REFRESH, user.refresh);
    }
    if (user.impersonatedUser) {
      this.setLocalStorage(IMPERSONATE_USER_ID, String(user.impersonatedUser));
    }
    if (user.originalUser) {
      this.setLocalStorage(IMPERSONATE_ORIGINAL_USER_ID, String(user.originalUser));
    }

    setUser({
      email: this.user.email,
    });

    if (import.meta.env.VITE_APP_STAGE !== 'e2e') {
      window.BugHerdConfig = {
        metadata: {
          'user.email': this.user.email,
        },
      };
    }
  }

  @action
  clear() {
    this.user = undefined;
    // Clean all cookies
    if (import.meta.env.VITE_APP_STAGE !== 'e2e') {
      this.clearCookie();
    }

    localStorage.removeItem(AUTH_TYPE);
    localStorage.removeItem(USER_EMAIL_KEY);
    localStorage.removeItem(KEY);
    localStorage.removeItem(REFRESH);
    setUser(null);
  }

  @computed
  get isLoggedIn() {
    return !!this.user;
  }

  @computed
  get auth_type() {
    return this.user?.auth_type ?? 'Token';
  }

  @computed
  get key() {
    return this.user?.key;
  }

  @computed
  get refresh() {
    return this.user?.refresh;
  }

  @computed
  get email() {
    return this.user?.email;
  }

  @computed
  get impersonatedUser() {
    return this.user?.impersonatedUser;
  }

  @computed
  get originalUser() {
    return this.user?.originalUser;
  }

  @action
  setCookieAccepted(value: Booleanish) {
    this.cookieAccepted = value;
    this.setLocalStorage(COOKIE_ACCEPTED, value);
  }

  @action
  setCookieDismissed(value: boolean) {
    this.cookieDismissed = value;
  }

  @action
  setImpersonatedUser(id: number) {
    this.user!.impersonatedUser = id;
    this.setLocalStorage(IMPERSONATE_USER_ID, String(id));
  }

  @action
  resetImpersonatedUser() {
    this.user!.impersonatedUser = null;
    localStorage.removeItem(IMPERSONATE_USER_ID);
  }

  @action
  setOriginalUser(id: number) {
    this.user!.originalUser = id;
    this.setLocalStorage(IMPERSONATE_ORIGINAL_USER_ID, String(id));
  }

  @action
  resetOriginalUser() {
    this.user!.originalUser = null;
    localStorage.removeItem(IMPERSONATE_ORIGINAL_USER_ID);
  }

  setIsLoggingOut(value: boolean) {
    this.isLoggingOut = value;
  }

  clearCookie() {
    if (typeof window === 'undefined') {
      return;
    }

    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  refreshToken = async () => {
    this.tokenPromise =
      this.tokenPromise ??
      fetch(`${getConfig().serverUrl}/jwt/token/refresh/`, {
        method: 'POST',
        body: JSON.stringify({ refresh: AuthManager.shared.refresh }),
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
        },
      }).then(async (response) => {
        if (response.ok) {
          const legalResponse = z.object({
            access: z.string(),
          });

          const data = await response.json();
          return legalResponse.parse(data).access;
        } else {
          throw new JwtExpiredError();
        }
      });

    return await this.tokenPromise;
  };

  async checkTokenRefresh() {
    if (AuthManager.shared.auth_type === 'Bearer') {
      const refresh = () =>
        this.refreshToken().then((token) => {
          AuthManager.shared.setUser({
            auth_type: AuthManager.shared.auth_type!,
            email: AuthManager.shared.email!,
            key: token,
            refresh: AuthManager.shared.refresh!,
            impersonatedUser: AuthManager.shared.impersonatedUser!,
            originalUser: AuthManager.shared.originalUser!,
          });

          this.tokenPromise = null;
        });

      try {
        const jwtDecoded = JWTDecoded.parse(jwtDecode(AuthManager.shared.key || ''));
        const { exp } = jwtDecoded;

        if (exp - Date.now() / 1000 <= 60) {
          await refresh();
        }
      } catch (e) {
        if (e instanceof InvalidTokenError || e instanceof ZodError) {
          console.error(e);
          // For invalid token status, we refresh it anyways.
          await refresh();
          return;
        }
        throw e;
      }
    }
  }
}
